import React, { useState, useRef, useEffect, useCallback } from 'react';
import Sidebar from '../../components/chat/Sidebar';
import Navbar from '../../components/navbar';
import { MdAutoAwesome, MdAttachFile, MdBolt } from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';

import { Box, Button, Flex, Text, Textarea, useColorMode, useColorModeValue, useToast } from '@chakra-ui/react';
import ChatResponse from '../../components/chat/ChatResponse';
import { saveChatHistory, getChatHistoryList, getChatHistory } from '../../utils/utils';
import { submitQuery } from '../../services/apiService';
import FixedPlugin from 'components/fixedPlugin/FixedPlugin';

const Chat = () => {
	// Add toast for better user feedback
	const toast = useToast();

	// Get authentication context
	const { user, userProfile, isAuthenticated } = useAuth();

	const [currentChatId, setCurrentChatId] = useState(null);
	const [chatHistoryList, setChatHistoryList] = useState([]);
	const [loadingHistory, setLoadingHistory] = useState(false);

	// Input States
	const [inputCode, setInputCode] = useState('');
	// Loading state
	const [loading, setLoading] = useState(false);

	// Add a state for chat responses
	const [chatResponses, setChatResponses] = useState([]);

	const { colorMode } = useColorMode();
	const borderColor = colorMode === 'light' ? 'blue.200' : 'whiteAlpha.200';
	// const inputColor = useColorModeValue('navy.700', 'white');
	const iconColor = useColorModeValue('brand.500', 'white');
	const brandColor = useColorModeValue('brand.500', 'gray.400');
	const textColor = useColorModeValue('gray.600', 'gray.400');
	const placeholderColor = useColorModeValue({ color: 'gray.500' }, { color: 'whiteAlpha.600' });
	const handleChange = (event) => {
		// Changes the css styling of rendered code
		setInputCode(event.target.value);
	};

	const [open, setOpen] = React.useState(window.innerWidth >= 1280);

	// Auto-collapse helper
	const handleMobileCollapse = useCallback(() => {
		if (window.innerWidth < 1280) {
			setOpen(false);
		}
	}, []);

	// Handle window resize
	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth >= 1280) {
				setOpen(true);
			} else {
				setOpen(false);
			}
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const currentRoute = 'Chat';

	const chatEndRef = useRef(null);

	const scrollToBottom = () => {
		chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		if (!loading) {
			scrollToBottom();
		}
	}, [chatResponses, loading]);

	// Helper to consistently determine the userId for API calls
	const getApiUserId = useCallback(() => {
		// Prefer the stable application userId from your Users table, then Cognito username, then loginId
		return (
			userProfile?.userId ||
			user?.username ||
			user?.signInDetails?.loginId ||
			'unknown'
		);
	}, [userProfile?.userId, user?.username, user?.signInDetails?.loginId]);

	// After login, load user's chat titles
	useEffect(() => {
		if (isAuthenticated && user) {
			const apiUserId = getApiUserId();
			(async () => {
				try {
					setLoadingHistory(true);
					const list = await getChatHistoryList(apiUserId);
					setChatHistoryList(Array.isArray(list) ? list : []);
				} catch (e) {
					console.error('Failed to load chat list', e);
				} finally {
					setLoadingHistory(false);
				}
			})();
		}
	}, [isAuthenticated, user, getApiUserId]);

	const genChatId = () => `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

	const handleTranslate = async () => {
		setLoading(true);

		if (!isAuthenticated) {
			toast({
				title: 'Authentication Error',
				description: 'Please sign in again.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			setLoading(false);
			return;
		}

		try {
			// The backend works out the company from the signed-in user, so none is sent.
			const jsonResponse = await submitQuery(inputCode);

			// Support both legacy { responses_by_source } and new { text_output, table_output }
			let formattedOutput = '';
			let sourcesLabel = '';

			const bySource = jsonResponse.responses_by_source;
			if (bySource && typeof bySource === 'object' && Object.keys(bySource).length > 0) {
				// Legacy path: responses_by_source
				formattedOutput = Object.entries(bySource)
					.map(([source, content]) => `**Source: ${source}**\n\n${content}`)
					.join('\n\n');
				sourcesLabel = Object.keys(bySource).join(', ');
			} else {
				// New path: text_output and/or table_output
				const sections = [];
				const labels = [];

				if (jsonResponse.text_output) {
					sections.push(`**Source: Summary**\n\n${jsonResponse.text_output}`);
					labels.push('Summary');
				}

				if (jsonResponse.table_output) {
					// Wrap CSV in a markdown code fence so ChatResponse renders a table
					const csvBlock = '```csv\n' + String(jsonResponse.table_output).trim() + '\n```';
					sections.push(`**Source: Table**\n\n${csvBlock}`);
					labels.push('Table');
				}

				formattedOutput = sections.join('\n\n');
				sourcesLabel = labels.length > 0 ? labels.join(', ') : '';
			}

			const newChatResponse = {
				inputOnSubmit: jsonResponse.query_text || inputCode,
				outputCode: formattedOutput || 'No response available.',
				sourcesCode: sourcesLabel || 'No sources available.',
			};

			console.log('New chat response:', newChatResponse); // Debug: Log the new chat response

			setChatResponses((prevResponses) => [...prevResponses, newChatResponse]);

			// Persist to backend (DynamoDB via API GW)
			try {
				const apiUserId = getApiUserId();
				let chatId = currentChatId;
				if (!chatId) {
					chatId = genChatId();
					setCurrentChatId(chatId);
				}
				const title = (inputCode || '').slice(0, 80) || 'Untitled';
				await saveChatHistory({ userId: apiUserId, chatId, title, message: inputCode, response: formattedOutput });
				if (!currentChatId) {
					// refresh list to include new chat
					const list = await getChatHistoryList(apiUserId);
					setChatHistoryList(Array.isArray(list) ? list : []);
				}
			} catch (e) {
				console.error('Failed to save chat history', e);
			}

			setLoading(false);
		} catch (error) {
			console.error('Error:', error);
			toast({
				title: 'Error',
				description: `Failed to get response: ${error.message}`,
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			setLoading(false);
		}
	};

	const handleSelectChat = async (chatId) => {
		const apiUserId = getApiUserId();
		try {
			setLoadingHistory(true);
			const data = await getChatHistory(apiUserId, chatId);
			const entries = data?.entries || data?.messages || [];
			const mapped = entries.map(e => ({
				inputOnSubmit: e.message || e.query || '',
				outputCode: e.response || e.answer || '',
				sourcesCode: e.sources || ''
			}));
			setChatResponses(mapped);
			setChatResponses(mapped);
			setCurrentChatId(chatId);
			handleMobileCollapse();
		} catch (e) {
			console.error('Failed to load chat', e);
		} finally {
			setLoadingHistory(false);
		}
	};

	const handleNewChat = () => {
		setChatResponses([]);
		setCurrentChatId(null);
		setInputCode('');
		handleMobileCollapse();
	};

	const handleSuggestedPromptClick = (prompt) => {
		setInputCode(prompt);
	};

	const navbarPosition = 'fixed';
	let navbarFilter = 'none';
	let navbarBackdrop = 'blur(20px)';
	let navbarShadow = 'none';
	let navbarBorder = 'transparent';
	let secondaryMargin = '0px';

	// const chatbarPosition = 'fixed';
	let chatbarFilter = 'none';
	let chatbarBackdrop = 'blur(20px)';
	let chatbarShadow = useColorModeValue('0 8px 32px 0 rgba(31, 38, 135, 0.15)', '0 8px 32px 0 rgba(0, 0, 0, 0.35)');
	let chatbarBorder = useColorModeValue('rgba(255, 255, 255, 0.4)', 'rgba(255, 255, 255, 0.1)');

	return (
		<>
			<div className='h-full w-full enable-text-selection'>
				<FixedPlugin />

				<Sidebar
					onSelectChat={handleSelectChat}
					open={open}
					onClose={() => setOpen(false)}
					chatHistoryList={chatHistoryList}
					currentChatId={currentChatId}
					onNewChat={handleNewChat}
					loadingHistory={loadingHistory}
				/>

				<Box
					className='bg-lightPrimary dark:bg-gray-900'
					// pt={{ base: '60px', md: '100px' }}
					float='right'
					minHeight='100vh'
					// height='100vh'
					position='relative'
					w={{ base: '100%', xl: 'calc( 100% - 290px )' }}
					maxWidth={{ base: '100%', xl: 'calc( 100% - 290px )' }}
					transition='all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)'
					transitionDuration='.2s, .2s, .35s'
					transitionProperty='top, bottom, width'
					transitionTimingFunction='linear, linear, ease'
					display='flex'
					flexDirection='column'>
					{/* <Portal> */}
					{/* <Box>
          </Box> */}
					{/* </Portal> */}

					{/* Scrollable Chat Content Area */}
					<Box
						flex='1'
					// overflowY='auto'
					// overflowX='hidden'
					// pt={{ base: '20px', md: '30px' }}
					// pb='250px' // Increased padding to allow content to scroll behind the fixed input area
					>


						<Box
							zIndex='100'
							position={navbarPosition}
							boxShadow={navbarShadow}
							borderColor={navbarBorder}
							filter={navbarFilter}
							backdropFilter={navbarBackdrop}
							backgroundPosition='center'
							backgroundSize='cover'
							borderRadius='16px'
							borderWidth='0px'
							borderStyle='solid'
							transitionDelay='0s, 0s, 0s, 0s'
							transitionDuration=' 0.25s, 0.25s, 0.25s, 0s'
							transition-property='box-shadow, background-color, filter, border'
							transitionTimingFunction='linear, linear, linear, linear'
							alignItems={{ xl: 'center' }}
							display={'block'}
							minH='75px'
							justifyContent={{ xl: 'center' }}
							mx='auto'
							mt={secondaryMargin}
							right={{ base: '12px', md: '30px', lg: '30px', xl: '30px' }}
							// top={{ base: '12px', md: '16px', xl: '18px' }}
							top='0px'
							p='30px'
							w={{
								base: 'calc(100vw - 8%)',
								md: 'calc(100vw - 8%)',
								lg: 'calc(100vw - 6%)',
								xl: 'calc(100vw - 335px)',
								'2xl': 'calc(100vw - 335px)',
							}}
						>
							<Navbar brandText={currentRoute} onOpenSidenav={() => setOpen(!open)} />
						</Box>


						<Flex className='chatResponseParent' w='100%' direction='column' position='relative'
							p={{ base: '20px', md: '30px' }} onClick={handleMobileCollapse}>
							{/* <ChatHistory messages={messages} /> */}
							<div className='chatResponseInner pt-20 pb-60'>
								{chatResponses.map((response, index) => (
									<ChatResponse key={index} inputOnSubmit={response.inputOnSubmit} outputCode={response.outputCode} sourcesCode={response.sourcesCode} borderColor={borderColor} brandColor={brandColor} textColor={textColor} iconColor={iconColor} />
								))}
								{/* </Flex> */}
							</div>
							<div className='scrollTobottom' ref={chatEndRef} />
						</Flex>
					</Box>

					{/* <Flex className='chatInputParent !z-5 fixed bottom-5 right-52 z-10 w-full  rounded-[20px] bg-white bg-clip-border shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:text-gray-200 dark:border-lime-100' direction='column' mx='auto' w={{ base: '100%', md: '100%', xl: '100%' }} maxW='1000px' overflowY='auto'> */}
					<Box
						className='bg-white/10 dark:!bg-navy-900/40  backdrop-blur-xl z-10'
						// position={chatbarPosition}
						position='fixed'
						boxShadow={chatbarShadow}
						borderColor={chatbarBorder}
						filter={chatbarFilter}
						backdropFilter={chatbarBackdrop}
						backgroundPosition='center'
						backgroundSize='cover'
						borderRadius='16px'
						borderWidth='1.5px'
						borderStyle='solid'
						transitionDelay='0s, 0s, 0s, 0s'
						transitionDuration='0.25s, 0.25s, 0.25s, 0s'
						transition-property='box-shadow, background-color, filter, border'
						transitionTimingFunction='linear, linear, linear, linear'
						alignItems={{ xl: 'center' }}
						display={'block'}
						minH='75px'
						justifyContent={{ xl: 'center' }}
						mx='auto'
						mt={secondaryMargin}
						right={'auto'}
						left={{
							base: '50%',
							xl: 'calc(50% + 145px)', // Center of remaining space (100vw - 290px sidebar)
						}}
						transform='translateX(-50%)'
						bottom={{ base: '12px', md: '16px', xl: '18px' }}
						w={{
							base: '95%',
							md: '85%',
							xl: '100%',
						}}
						maxW='1000px'
					>
						<Flex className='chatInputParent' shadow={'none'} width={'full'} p={5} align='center' justifySelf={'flex-end'}>
							<Textarea
								className='chatInputItem'
								minH='54px'
								h='54px'
								border='2px solid'
								borderColor={'blue.400'}
								borderRadius='45px'
								py='15px'
								px='25px'
								me='10px'
								fontSize='sm'
								fontWeight='500'
								_focus={{ boxShadow: 'none' }}
								// color={textColor}
								_placeholder={placeholderColor}
								placeholder={
									!isAuthenticated || !userProfile
										? 'Please wait for user authentication to complete...'
										: 'Type your message here...'
								}
								onChange={handleChange}
								value={inputCode}
								isDisabled={!isAuthenticated || !userProfile}
								overflow='hidden'
							/>

							<Button
								variant='primary'
								textColor={'white'}
								bg='linear-gradient(15.46deg, #4A25E1 26.3%, #7B5AFF 86.4%) !important'
								py='20px'
								px='16px'
								fontSize='sm'
								borderRadius='45px'
								ms='auto'
								w={{ base: '160px', md: '210px' }}
								h='54px'
								_hover={{
									boxShadow: '0px 21px 27px -10px rgba(96, 60, 255, 0.48) !important',
									bg: 'linear-gradient(15.46deg, #4A25E1 26.3%, #7B5AFF 86.4%) !important',
									_disabled: {
										bg: 'linear-gradient(15.46deg, #4A25E1 26.3%, #7B5AFF 86.4%)',
									},
								}}
								onClick={handleTranslate}
								isLoading={loading ? true : false}
								isDisabled={!isAuthenticated || !userProfile || loading}>
								Submit
							</Button>
						</Flex>

						{/* Centered prompt cards */}
						<div className="w-full py-4">
							<div className="mx-auto max-w-3xl flex justify-center">
								<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
									{/* Card 1 */}
									<div
										className="flex cursor-pointer flex-col items-center rounded-xl bg-white p-3 shadow-lg transition-all hover:shadow-xl dark:bg-navy-800"
										onClick={() => handleSuggestedPromptClick('Generate a report on excavation materials')}
									>
										<div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
											<MdAutoAwesome className="text-xl text-blue-500" />
										</div>
										<h3 className="text-center text-sm font-semibold text-blue-800 dark:text-blue-200">
											Generate a report on excavation materials
										</h3>
									</div>

									{/* Card 2 */}
									<div
										className="flex cursor-pointer flex-col items-center rounded-xl bg-white p-3 shadow-lg transition-all hover:shadow-xl dark:bg-navy-800"
										onClick={() => handleSuggestedPromptClick('Generate a report on groundworks and substructure')}
									>
										<div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
											<MdBolt className="text-xl text-green-500" />
										</div>
										<h3 className="text-center text-sm font-semibold text-green-800 dark:text-green-200">
											Generate a report on groundworks and substructure
										</h3>
									</div>

									{/* Card 3 */}
									<div
										className="flex cursor-pointer flex-col items-center rounded-xl bg-white p-3 shadow-lg transition-all hover:shadow-xl dark:bg-navy-800"
										onClick={() => handleSuggestedPromptClick('What is the Disposal of materials off-site?')}
									>
										<div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
											<MdAttachFile className="text-xl text-orange-500" />
										</div>
										<h3 className="text-center text-sm font-semibold text-orange-800 dark:text-orange-200">
											What is the Disposal of materials off-site?
										</h3>
									</div>
								</div>
							</div>
						</div>

						<Flex justify='center' p={3} direction={{ base: 'column', md: 'row' }} alignItems='center'>
							<Text fontSize='xs' textAlign='center'>
								Free Research Preview. AI may produce inaccurate information about people, places, or facts.
							</Text>
						</Flex>
					</Box>
					{/* Fixed Input Area - End */}

				</Box>
			</div>
		</>
	);
};

export default Chat;
